import { useState } from "react";
import { toast } from "sonner";
import { Share2, Mail, Copy, Instagram } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { useLang } from "../contexts/LanguageContext";

/**
 * Reusable Share button + popover with the big 5 social platforms + copy link.
 *
 * Props:
 *   - url:     the canonical share URL (server-rendered /share endpoint)
 *   - title:   plain-text title for tweet/email bodies
 *   - testid:  base data-testid (e.g. "photo-share" or "hub-share")
 *   - className?: optional extra classes for the trigger button
 *   - onShare?: optional callback fired once per action (key passed). Used by
 *               PhotoDetail to bump the photo's share counter.
 */
export default function ShareMenu({ url, title, testid = "share", className = "", onShare }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);

  const enc = encodeURIComponent;
  const shareText = `${title}`;

  const targets = [
    {
      key: "facebook",
      label: "Facebook",
      Icon: FacebookIcon,
      href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
    },
    {
      key: "x",
      label: "X",
      Icon: XIcon,
      href: `https://twitter.com/intent/tweet?text=${enc(shareText)}&url=${enc(url)}`,
    },
    {
      key: "whatsapp",
      label: "WhatsApp",
      Icon: WhatsAppIcon,
      href: `https://api.whatsapp.com/send?text=${enc(`${shareText} ${url}`)}`,
    },
    {
      key: "email",
      label: "Email",
      Icon: Mail,
      href: `mailto:?subject=${enc(title)}&body=${enc(`${shareText}\n\n${url}`)}`,
    },
  ];

  const openTarget = (href, key) => {
    window.open(href, "_blank", "noopener,noreferrer,width=600,height=600");
    onShare?.(key);
    setOpen(false);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("share_copied"));
      onShare?.("copy");
    } catch {
      toast.error(t("share_copy_failed"));
    }
    setOpen(false);
  };

  // Instagram has no web-share intent; copy + tell the user to paste in IG.
  const shareToInstagram = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("share_instagram_hint"));
      onShare?.("instagram");
    } catch {
      toast.error(t("share_copy_failed"));
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={`inline-flex items-center gap-2 border border-zinc-800 text-zinc-200 px-5 py-2 text-xs uppercase tracking-[0.3em] hover:bg-zinc-900 hover:border-zinc-700 transition-colors ${className}`}
          data-testid={`${testid}-button`}
          aria-label={t("share")}
        >
          <Share2 size={12} />
          {t("share")}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-64 p-2 bg-zinc-950 border border-zinc-800 text-zinc-100"
        data-testid={`${testid}-menu`}
      >
        <div className="px-2 pt-1 pb-2 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
          {t("share_to")}
        </div>
        <ul className="flex flex-col">
          {targets.map(({ key, label, Icon, href }) => (
            <li key={key}>
              <button
                onClick={() => openTarget(href, key)}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-zinc-200 hover:bg-zinc-900 transition-colors"
                data-testid={`${testid}-${key}`}
              >
                <span className="w-5 h-5 inline-flex items-center justify-center text-zinc-300">
                  <Icon size={16} />
                </span>
                <span>{label}</span>
              </button>
            </li>
          ))}
          <li>
            <button
              onClick={shareToInstagram}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-zinc-200 hover:bg-zinc-900 transition-colors"
              data-testid={`${testid}-instagram`}
            >
              <span className="w-5 h-5 inline-flex items-center justify-center text-zinc-300">
                <Instagram size={16} />
              </span>
              <span>Instagram</span>
            </button>
          </li>
          <li className="mt-1 border-t border-zinc-900">
            <button
              onClick={copyLink}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-zinc-200 hover:bg-zinc-900 transition-colors"
              data-testid={`${testid}-copy`}
            >
              <span className="w-5 h-5 inline-flex items-center justify-center text-zinc-300">
                <Copy size={16} />
              </span>
              <span>{t("copy_link")}</span>
            </button>
          </li>
        </ul>
      </PopoverContent>
    </Popover>
  );
}

/* ---------------- Inline brand-mark SVGs (single-color, currentColor) ----------------
   We don't use lucide for these because lucide doesn't ship the official Meta /
   X / WhatsApp glyphs. These are simplified single-path versions sized at 16px
   so they sit cleanly next to lucide icons. */

function FacebookIcon({ size = 16 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <path d="M13.5 21.95v-7.7h2.6l.4-3h-3v-1.9c0-.87.24-1.46 1.5-1.46h1.6V5.13a21 21 0 0 0-2.34-.12c-2.32 0-3.91 1.42-3.91 4.02v2.22H7.74v3h2.61v7.7h3.15Z" />
    </svg>
  );
}

function XIcon({ size = 16 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <path d="M18.244 3H21l-6.52 7.45L22 21h-6.39l-4.78-6.43L5.3 21H2.55l6.97-7.97L2 3h6.5l4.34 5.86L18.244 3Zm-2.24 16.2h1.7L7.93 4.7H6.13l9.87 14.5Z" />
    </svg>
  );
}

function WhatsAppIcon({ size = 16 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <path d="M12.04 2C6.59 2 2.16 6.43 2.16 11.88a9.77 9.77 0 0 0 1.34 4.96L2 22l5.32-1.4a9.95 9.95 0 0 0 4.71 1.2h.01c5.45 0 9.88-4.43 9.88-9.88S17.49 2 12.04 2Zm5.78 14.13c-.24.68-1.4 1.32-1.93 1.4-.5.07-1.13.1-1.83-.12a16.7 16.7 0 0 1-1.66-.62c-2.92-1.27-4.83-4.23-4.98-4.42-.14-.2-1.18-1.57-1.18-3 0-1.42.74-2.12 1-2.41.27-.3.59-.37.78-.37l.56.01c.18 0 .43-.07.67.51.24.6.83 2.06.9 2.21.07.15.12.32.02.51-.1.2-.14.31-.29.48-.14.17-.3.37-.43.5-.14.14-.29.3-.13.59.17.3.74 1.22 1.59 1.97 1.09.97 2.01 1.27 2.3 1.42.29.14.46.12.63-.07.18-.2.73-.85.92-1.14.2-.3.39-.25.66-.15.27.1 1.71.81 2 .96.3.14.49.22.56.34.07.12.07.69-.17 1.36Z" />
    </svg>
  );
}
